import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, colors, fontWeight} from '@/styles/tokens';

export interface RegulatoryItem {
    country: string;
    license: string;
    description: string;
}

interface WhiteLabelRegulatorySupportProps {
    heading?: string;
    subheading?: string;
    body?: string;
    data: RegulatoryItem[];
}

const cardSx = {
    bgcolor: colors.background.default,
    border: '1px solid rgba(0,0,0,0.13)',
    borderRadius: borderRadius.lg,
    p: '33px',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
} as const;

const topAccentSx = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(to right, rgba(37,99,235,0.2), rgba(37,99,235,0))'
} as const;

const badgeSx = {
    bgcolor: 'rgba(31,63,122,0.1)',
    border: '1px solid rgba(31,63,122,0.2)',
    borderRadius: borderRadius.sm,
    px: '13px',
    py: '7px',
    flexShrink: 0
} as const;

const WhiteLabelRegulatorySupport = ({
    heading = 'Compliance & Regulatory Support',
    subheading = 'Launch Compliant in Your Jurisdiction — We Know the Rules',
    body = "Regulatory compliance is the most complex part of launching a crypto exchange — and the most consequential. Our team has guided exchanges through compliance requirements across five major regulatory environments. We don't leave you to navigate it alone.",
    data
}: WhiteLabelRegulatorySupportProps) => {
    return (
        <MUIContainer>
            <Box textAlign="center" maxWidth={900} margin="0 auto" mb={{xs: 5, md: 7}}>
                <Typography variant="h2" component="h2" mb={2} fontSize={{xs: 32, sm: 52}} className="font-headers" fontWeight={800}>
                    {heading}
                </Typography>
                <Typography
                    variant="h3"
                    component="p"
                    mb={2.5}
                    fontSize={{xs: 16, sm: 22}}
                    className="font-headers"
                    fontWeight={700}
                    color="#0f1724"
                >
                    {subheading}
                </Typography>
                <Typography variant="body1" className="font-regular-text" sx={{fontSize: 16, color: colors.text.primary, lineHeight: 1.75}}>
                    {body}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {data.map((item, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Box sx={cardSx}>
                            <Box sx={topAccentSx} />

                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography
                                    className="font-headers"
                                    sx={{fontWeight: fontWeight.bold, fontSize: 24, color: '#0f1724', lineHeight: '36px'}}
                                >
                                    {item.country}
                                </Typography>
                                <Box sx={badgeSx}>
                                    <Typography
                                        className="font-headers"
                                        sx={{
                                            fontWeight: fontWeight.semibold,
                                            fontSize: 13,
                                            color: colors.primary.deep,
                                            lineHeight: '19.5px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {item.license}
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography className="font-regular-text" sx={{fontSize: 15, color: colors.text.primary, lineHeight: '22.5px'}}>
                                {item.description}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </MUIContainer>
    );
};

export default memo(WhiteLabelRegulatorySupport);
